import { useFlagsmith, useFlagsmithLoading } from "flagsmith/react";
import { useAtomValue, useStore } from "jotai";
import { useRouter } from "next/router";
import * as React from "react";
import { useContext, useEffect, useMemo } from "react";
import { SessionTimeoutModal } from "../components/modals/isIdleModal/SessionTimeoutModal";
import {
  SESSION_INACTIVITY_MS,
  SESSION_WARNING_MS
} from "../constants/sessionTimeout";
import { InterfaceSessionContext, SessionContext } from "../context";
import { useFeatureFlags } from "../featureFlags/useFeatureFlags";
import {
  isUserUploadingAtom,
  isUserWatchingAtom,
  sessionActivityAtom
} from "../stateManagement/atoms/timeout.atom";
import { useProfile } from "./profileProvider/ProfileProvider";

export const useSession = () =>
  useContext<InterfaceSessionContext>(SessionContext);

export const SessionProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const isIdle = useAtomValue(sessionActivityAtom).phase === "idle";
  const sessionContextValue = useMemo(() => ({ isIdle }), [isIdle]);

  return (
    <SessionContext.Provider value={sessionContextValue}>
      {/* PPC-969: Idle detection remains to support editor-lock behavior after timer removal. */}
      <SessionActivity />
      {children}
    </SessionContext.Provider>
  );
};

const SessionActivity = () => {
  const { profile } = useProfile();
  const flagsmith = useFlagsmith();
  const loading = useFlagsmithLoading();

  // The shared flag hook assumes a loaded profile; keep its consumers behind this boundary.
  if (
    !profile?.sub ||
    !flagsmith?.initialised ||
    loading?.isLoading !== false
  ) {
    return null;
  }

  return <ReadySessionActivity key={profile.sub} />;
};

const ReadySessionActivity = () => {
  const { query, pathname } = useRouter();
  const { session_timeout_modal } = useFeatureFlags(["session_timeout_modal"]);
  const store = useStore();
  const isWatching = useAtomValue(isUserWatchingAtom);
  const isUploading = useAtomValue(isUserUploadingAtom);
  const studio = pathname === "/studio" || pathname === "/studio-v2";
  const videoId = String(
    query.translatedVideoID ?? query.translatedVideoId ?? ""
  );
  const showWarning = studio && !!session_timeout_modal?.enabled;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let deadline = Date.now() + SESSION_INACTIVITY_MS;
    const exempt = isWatching || isUploading;
    store.set(sessionActivityAtom, { phase: "active", deadline: null });

    const reconcile = () => {
      clearTimeout(timer);
      const current = store.get(sessionActivityAtom);
      if (exempt || current.phase === "resuming") return;
      if (showWarning && current.phase === "idle") return;

      const now = Date.now();
      if (now < deadline) {
        timer = setTimeout(reconcile, deadline - now);
        return;
      }

      if (!showWarning || now >= deadline + SESSION_WARNING_MS) {
        store.set(sessionActivityAtom, { phase: "idle", deadline: null });
        return;
      }

      if (
        current.phase !== "warning" ||
        current.deadline !== deadline + SESSION_WARNING_MS
      ) {
        store.set(sessionActivityAtom, {
          phase: "warning",
          deadline: deadline + SESSION_WARNING_MS
        });
      }
      timer = setTimeout(reconcile, deadline + SESSION_WARNING_MS - now);
    };

    const onActivity = () => {
      // Reconcile an overdue background deadline before allowing input to reset it.
      reconcile();
      const current = store.get(sessionActivityAtom);
      if (showWarning && current.phase !== "active") return;
      deadline = Date.now() + SESSION_INACTIVITY_MS;
      if (current.phase !== "active") {
        store.set(sessionActivityAtom, { phase: "active", deadline: null });
      }
      reconcile();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") onActivity();
    };
    const unsubscribe = store.sub(sessionActivityAtom, () => {
      if (store.get(sessionActivityAtom).phase === "active") {
        deadline = Date.now() + SESSION_INACTIVITY_MS;
        reconcile();
      }
    });
    const events = [
      "mousemove",
      "mousedown",
      "resize",
      "keydown",
      "touchstart",
      "wheel"
    ];
    for (const event of events) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisible);
    reconcile();

    return () => {
      clearTimeout(timer);
      unsubscribe();
      for (const event of events) {
        window.removeEventListener(event, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [store, showWarning, studio, videoId, isWatching, isUploading]);

  return <SessionTimeoutModal />;
};
